import * as Sentry from '@sentry/react';

// ----------------------------------------------------------------------

type AxiosLikeError = {
  message?: string;
  config?: {
    method?: string;
    url?: string;
    baseURL?: string;
    data?: unknown;
  };
  response?: {
    status: number;
    statusText?: string;
    data?: unknown;
  };
  code?: string;
};

function asAxiosLike(error: unknown): AxiosLikeError | null {
  if (typeof error !== 'object' || error === null) return null;
  return error as AxiosLikeError;
}

/**
 * Reports failed API calls to Sentry after axios retries / auth refresh have been exhausted.
 * Skips 401 to avoid noise from normal session expiry.
 */
export function reportAxiosError(error: unknown): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  const err = asAxiosLike(error);
  if (!err) {
    Sentry.captureException(error, { tags: { error_source: 'axios' } });
    return;
  }

  const status = err.response?.status;
  if (status === 401) return;

  Sentry.withScope((scope) => {
    scope.setTag('error_source', 'axios');
    if (status != null) {
      scope.setTag('http_status', String(status));
    } else {
      scope.setTag('http_status', 'none');
    }
    if (err.code) {
      scope.setTag('axios_code', err.code);
    }

    const cfg = err.config;
    if (cfg) {
      scope.setContext('request', {
        method: (cfg.method ?? 'get').toUpperCase(),
        url: cfg.url,
        baseURL: cfg.baseURL,
      });
    }

    if (err.response) {
      const { status: st, statusText, data } = err.response;
      let body: unknown = data;
      if (typeof data === 'string' && data.length > 2000) {
        body = { truncated: true, length: data.length };
      }
      scope.setContext('response', {
        status: st,
        statusText,
        data: body,
      });
    }

    Sentry.captureException(error);
  });
}
